import React from "react";
import { useRef, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setConversationStartIndex } from "../../redux/conversationStartIndexSlice";

const options = [
  { value: "1000", text: "start from the very beginning" },
  { value: "3", text: "Start from the last 3 questions" },
  { value: "2", text: "Start from the last 2 questions" },
  { value: "1", text: "Start from the last 1 question" },
  { value: "0", text: "start from the next question" },
];

function ConversationSelect() {
  const dropDownRef = useRef(null);
  const dropDownContainerRef = useRef(null);
  const [conversationMode, setConversationMode] = useState("off");
  const [conversationStart, setConversationStart] = useState("0");
  const dispatch = useDispatch();
  const messages = useSelector((state) => state.messages);
  const conversationId = useSelector((state) => state.conversationId);
  const conversationStartIndex = useSelector(
    (state) => state.conversationStartIndex
  );
  const [dropdownPosition, setDropdownPosition] = useState("");

  const handleDropdownClick = () => {
    if (dropDownContainerRef.current) {
      const rect = dropDownContainerRef.current.getBoundingClientRect();
      const spaceBelow = Math.floor(window.innerHeight - rect.bottom - 10);
      setDropdownPosition(`${spaceBelow}px`);
    }
    dropDownRef.current.classList.toggle("hidden");
  };

  const handleClickOutside = (e) => {
    if (
      dropDownContainerRef.current &&
      !dropDownContainerRef.current.contains(e.target)
    ) {
      dropDownRef.current.classList.add("hidden");
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  });

  useEffect(() => {
    // reset two states when conversationId changes
    setConversationMode("off");
    setConversationStart("0");
    dispatch(setConversationStartIndex(100000));
  }, [conversationId, dispatch]);

  let buttonText = "";
  let fontSize = "";
  if (conversationMode === "off") {
    buttonText = "No context";
  } else if (conversationStartIndex === messages.length) {
    buttonText = "Start from the next question";
  } else {
    buttonText = messages[conversationStartIndex]?.content ?? "None";
    fontSize = "text-xs";
  }

  return (
    <>
      <div
        className="relative select-none h-9"
        onClick={handleDropdownClick}
        ref={dropDownContainerRef}
      >
        <div
          className={`flex relative items-center text-white text-sm pl-2 pr-8  h-full bg-[hsla(var(--bg-300))] rounded-md hover:bg-gray-700 hover:text-white cursor-pointer transition border-[1px] ${
            conversationMode === "on"
              ? "border-[#FC0]"
              : "border-[hsla(var(--border-100))]"
          }`}
        >
          {/* Context: {conversationMode} */}
          <div className={"line-clamp-1 " + fontSize}>{buttonText}</div>
          <ArrowDown />
        </div>
        <DropDown
          dropDownRef={dropDownRef}
          setConversationMode={setConversationMode}
          conversationMode={conversationMode}
          conversationStart={conversationStart}
          setConversationStart={setConversationStart}
          messages={messages}
          dropdownPosition={dropdownPosition}
        />
      </div>
    </>
  );
}

const ArrowDown = function () {
  return (
    <div className="absolute right-4 top-[57%] -translate-y-1/2">
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g id="SVGRepo_iconCarrier">
          {" "}
          <path
            d="M19 9L14 14.1599C13.7429 14.4323 13.4329 14.6493 13.089 14.7976C12.7451 14.9459 12.3745 15.0225 12 15.0225C11.6255 15.0225 11.2549 14.9459 10.9109 14.7976C10.567 14.6493 10.2571 14.4323 10 14.1599L5 9"
            stroke="hsla(var(--text-100)/0.8)"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          ></path>{" "}
        </g>
      </svg>
    </div>
  );
};

const DropDown = ({
  dropDownRef,
  setConversationMode,
  conversationMode,
  conversationStart,
  setConversationStart,
  messages,
  dropdownPosition,
}) => {
  const dispatch = useDispatch();
  return (
    <div
      className={`absolute w-full overflow-y-scroll top-full right-0 z-10 border py-3 bg-[hsla(var(--bg-100))] hidden rounded-lg`}
      ref={dropDownRef}
      style={{ maxHeight: dropdownPosition }}
    >
      <div className="px-4 text-gray-500 mb-2 text-sm font-semibold">
        Choose where the conversation begins:{" "}
      </div>
      <div
        className="hover:bg-[hsla(var(--bg-300))] cursor-pointer px-4 py-1"
        onClick={() => {
          setConversationMode("off");
          setConversationStart("0");
          dispatch(setConversationStartIndex(100000));
        }}
      >
        <div>no context</div>
      </div>
      {options.map((option) => {
        const startIndex = getStartIndex(messages, "on", option.value);
        if (startIndex > 0 || option.value === "1000") {
          return (
            <div
              className="hover:bg-[hsla(var(--bg-300))] cursor-pointer px-4 py-1"
              onClick={() => {
                setConversationMode("on");
                setConversationStart(option.value);
                dispatch(setConversationStartIndex(startIndex));
              }}
              key={option.value}
            >
              <div>{option.text} </div>
              <div className="text-sm text-gray-500 line-clamp-2">
                {messages[startIndex]?.content}
              </div>
            </div>
          );
        } else {
          return null;
        }
      })}
    </div>
  );
};

const getStartIndex = (messages, mode, startFrom) => {
  if (mode === "off") {
    return 100000;
  } else {
    let i = messages.length;
    let count_user = 0;
    while (i >= 0 && count_user < parseInt(startFrom)) {
      i -= 1;
      if (i >= 0 && messages[i].role === "user") {
        count_user += 1;
      }
    }
    return Math.max(i, 0);
  }
};

export default ConversationSelect;
